import { linePath } from './chartUtils';

export interface SparklineProps {
  values: Array<number | null>;
  width?: number;
  height?: number;
  color?: string;
  /** A dashed reference line drawn in the same scale as the values. */
  target?: number;
}

/**
 * A bare trend cue for stat tiles and table rows — no axes, no tooltip.
 *
 * Nulls are dropped from the line rather than plotted as zero, so a month with
 * no result leaves a gap instead of a crash to the floor. Fewer than two real
 * values renders nothing: one point is not a trend, and a single dot invites
 * being read as one.
 */
export function Sparkline({
  values,
  width = 92,
  height = 26,
  color = 'var(--series-1)',
  target,
}: SparklineProps) {
  const clean = values.filter((v): v is number => Number.isFinite(v as number));
  if (clean.length < 2) return <svg width={width} height={height} aria-hidden="true" />;

  /* The target is included in the extent so a line far from it does not sit off
     the top of the box with the reference line invisible. */
  const min = Math.min(...clean, target ?? Infinity);
  const max = Math.max(...clean, target ?? -Infinity);
  const span = max - min || 1;
  const pad = 3;

  const x = (i: number) => (i / Math.max(values.length - 1, 1)) * (width - 2);
  const y = (v: number) => pad + (1 - (v - min) / span) * (height - pad * 2);

  const points = values
    .map((v, i) => (Number.isFinite(v as number) ? ([x(i), y(v as number)] as [number, number]) : null))
    .filter((p): p is [number, number] => p !== null);

  const last = points[points.length - 1];

  return (
    <svg width={width} height={height} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      {target !== undefined && (
        <line
          x1={0}
          x2={width}
          y1={y(target)}
          y2={y(target)}
          strokeWidth={1}
          strokeDasharray="3 3"
          /* Through style, not a stroke attribute — see the note in chartUtils. */
          style={{ stroke: 'var(--text-muted)' }}
        />
      )}
      <path
        d={linePath(points)}
        fill="none"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ stroke: color }}
      />
      {last && (
        <circle
          cx={last[0]}
          cy={last[1]}
          r={2.75}
          strokeWidth={1.5}
          /* Ringed in the card colour so the head reads as a dot on the line
             rather than merging with it where the line doubles back. */
          style={{ fill: color, stroke: 'var(--surface-card)' }}
        />
      )}
    </svg>
  );
}
